import { getDispatchControlBoolean, getPayrollControlBoolean } from "@mcleod/general";
import { AutogenLayoutOtherChargeCodes } from "./autogen/AutogenLayoutOtherChargeCodes";
import {
    ChangeEvent, ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, LookupModelSelectionEvent
} from "@mcleod/components";
import { CurrencySettings } from "@mcleod/core";
import { DuplicateId, DuplicateIdProps } from "@mcleod/dispatch";
import { getExciseTaxControlBoolean } from "../../ar/src/models/ModelExciseTaxControl";

const viewCodeURL = "lme/dispatch/OtherChargeCodes?mode=update&key=";

const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "charge_code",
    screenName: "Charge Code",
    navigateToUrl: viewCodeURL
};

export class OtherChargeCodes extends AutogenLayoutOtherChargeCodes {
    override async onLoad(): Promise <void> {
        DuplicateId.configureIdTextbox(this.textboxId, DUPLICATE_VALIDATION_PROPS);
        const multicurrency = CurrencySettings.getSingleton().multiCurrencyEnabled();
        this.checkboxIsIntermodal.visible = getDispatchControlBoolean("is_container_active", false);
        this.checkboxExcludeCommissions.visible = getPayrollControlBoolean("enable_commissions", false);
        if (multicurrency) {
            this.checkboxIsTaxable.visible = await this.isExcise();
            this.checkboxIsBackhaulTax.visible = await this.isExcise();
        } else {
            this.checkboxIsTaxable.visible = false;
            this.checkboxIsBackhaulTax.visible = false;
        }
    }

    sourceChargeCodeAfterExecution(event: DataSourceExecutionEvent) {
        if (event?.getAction() == DataSourceAction.ADD || event?.getAction() == DataSourceAction.UPDATE ||
            event?.getAction() == DataSourceAction.SEARCH) {
            if (this.checkboxIsTonu.checked) {
                this.textboxChargeRateMethod.enabled = false;
                this.textboxChargeRateMethod.setSelectedItemFromValue("F");
                this.textboxWhoToPay.enabled = false;
                this.textboxWhoToPay.setSelectedItemFromValue("P");
            } else {
                this.textboxChargeRateMethod.enabled = true;
                this.textboxWhoToPay.enabled = true;
            }
            this.enableDisableBackhaulTax();
        }
    }

    isClickedTONU(event: ClickEvent) {
        if (this.checkboxIsTonu.checked) {
            this.textboxChargeRateMethod.enabled = false;
            this.textboxChargeRateMethod.setSelectedItemFromValue("F");
            this.textboxWhoToPay.enabled = false;
            this.textboxWhoToPay.setSelectedItemFromValue("P");
        } else {
            this.textboxChargeRateMethod.enabled = true;
            this.textboxWhoToPay.enabled = true;
        }
    }

    checkboxIsTaxableOnClick(event: ClickEvent) {
        this.enableDisableBackhaulTax();
    }

    checkboxIsBackhaulTaxOnClick(event: ClickEvent) {
        this.handleBackhaulTaxChange();
    }

    changeRateMethodOnChange(event: ChangeEvent) {
        if (this.textboxChargeRateMethod.selectedItem?.value == "P") {
            this.textboxAmount.text = null;
            this.textboxAmount.enabled = false;
            this.textboxRate.enabled = true;
            this.checkboxPrevFlag.enabled = true;
        } else {
            this.textboxRate.text = null;
            this.textboxAmount.enabled = true;
            this.textboxRate.enabled = false;
            this.checkboxPrevFlag.enabled = false;
        }
    }

    cOMethodOnChange(event: ChangeEvent) {
        switch (this.textboxCoPayMethod.selectedItem?.value) {
            case ("P"):
                this.textboxCoAmount.text = null;
                this.textboxCoAmount.enabled = false;
                this.textboxCoPercent.enabled = true;
                break;
            case ("F"):
                this.textboxCoPercent.text = null;
                this.textboxCoAmount.enabled = true;
                this.textboxCoPercent.enabled = false;
                break;
            default:
                this.textboxCoAmount.text = null;
                this.textboxCoPercent.text = null;
                this.textboxCoAmount.enabled = false;
                this.textboxCoPercent.enabled = false;
        }
        if (!(this.textboxWhoToPay.selectedItem?.value == "N")) {
            if (this.textboxCoPayMethod.selectedItem?.value != null) {
                this.textboxCoDeductCodeId.required = true;
            }
        }
    }

    oOMethodOnChange(event: ChangeEvent) {
        switch (this.textboxOoPayMethod.selectedItem?.value) {
            case ("P"):
                this.textboxOoAmount.text = null;
                this.textboxOoAmount.enabled = false;
                this.textboxOoPercent.enabled = true;
                break;
            case ("F"):
                this.textboxOoPercent.text = null;
                this.textboxOoAmount.enabled = true;
                this.textboxOoPercent.enabled = false;
                break;
            default:
                this.textboxOoAmount.text = null;
                this.textboxOoPercent.text = null;
                this.textboxOoAmount.enabled = false;
                this.textboxOoPercent.enabled = false;
        }
        if (!(this.textboxWhoToPay.selectedItem?.value == "N")) {
            if (this.textboxOoPayMethod.selectedItem?.value != null) {
                this.textboxOoDeductCodeId.required = true;
            }
        }
    }

    textboxCoDeductCodeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.activeRow.set("co_gl_account.id", event.selection?.getFirstLookupModelData("glid")?.get("id", null));
        this.activeRow.set("co_gl_account.descr", event.selection?.getFirstLookupModelData("glid")?.get("descr", null));
    }

    textboxOoDeductCodeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.activeRow.set("oo_gl_account.id", event.selection?.getFirstLookupModelData("glid")?.get("id", null));
        this.activeRow.set("oo_gl_account.descr", event.selection?.getFirstLookupModelData("glid")?.get("descr", null));
    }

    async isExcise() {
        return getExciseTaxControlBoolean("is_active", false);
    }

    enableDisableBackhaulTax() {
        if (this.sourceChargeCode.mode === DataSourceMode.NONE) {
            return;
        }
        const taxableChecked = this.checkboxIsTaxable.checked;
        this.checkboxIsBackhaulTax.enabled = taxableChecked;
        if (!taxableChecked) {
            this.checkboxIsBackhaulTax.checked = false;
        }
    }

    handleBackhaulTaxChange() {
        if (this.sourceChargeCode.mode === DataSourceMode.NONE) {
            return;
        }
        if (this.checkboxIsBackhaulTax.checked) {
            this.checkboxInclInFreight.checked = false;
            this.checkboxInclInFreight.enabled = false;
        } else {
            this.checkboxInclInFreight.enabled = true;
        }
    }
}




